import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useState,
} from "react";
import PropTypes from "prop-types";
import { first } from "lodash";
import { useLoadObjectiveBranch } from "./hooks";
import { GOALS_ACTIONS } from "./actionTypes";
import { goalsReducer, initialState } from "./reducers";

const GoalsExploreContext = createContext();

export const useGoalsExploreContext = () => {
  const context = useContext(GoalsExploreContext);
  if (!context) {
    throw new Error(
      "useGoalsExploreContext must be used within GoalsExploreProvider"
    );
  }
  return context;
};

const GoalsExploreProvider = ({ children }) => {
  const [state, dispatch] = useReducer(goalsReducer, initialState);
  const [loadingNodeIds, setLoadingNodeIds] = useState({});

  const handleRootDataLoaded = useCallback(
    (rootTeams) => (data) => {
      const objectives = data?.searchObjectives?.objectives || [];
      const associatedTeams = data?.searchObjectives?.associatedTeams || [];
      dispatch({
        type: GOALS_ACTIONS.LOAD_ROOT_OBJECTIVES,
        payload: { objectives, rootTeam: first(rootTeams), associatedTeams },
      });
    },
    []
  );

  const handleAppendRootData = useCallback((data) => {
    const objectives = data?.searchObjectives?.objectives || [];
    const associatedTeams = data?.searchObjectives?.associatedTeams || [];

    dispatch({
      type: GOALS_ACTIONS.APPEND_ROOT_OBJECTIVES,
      payload: { objectives, associatedTeams },
    });
  }, []);

  const handleBranchDataLoaded = useCallback((data, parentNodeId) => {
    const objectives = data?.searchObjectives?.objectives || [];
    const associatedTeams = data?.searchObjectives?.associatedTeams || [];

    dispatch({
      type: GOALS_ACTIONS.LOAD_BRANCH_OBJECTIVES,
      payload: { objectives, parentNodeId, associatedTeams },
    });
  }, []);

  const [loadBranchQuery, { loading: branchLoading }] =
    useLoadObjectiveBranch();

  const loadBranch = useCallback(
    ({
      nodeId,
      maxChildDepth = 1,
      excludeBaseObjectives = false,
      includingChildObjectives = true,
      includingParentObjectives = true,
      includeAssociatedTeams = false,
    }) => {
      const objectiveIds = nodeId ? [nodeId] : [];

      if (nodeId) {
        setLoadingNodeIds((prev) => ({ ...prev, [nodeId]: true }));
      }

      loadBranchQuery({
        objectiveIds,
        maxChildDepth,
        excludeBaseObjectives,
        includeAssociatedTeams,
        inputOverrides: {
          includeChildObjectives: includingChildObjectives,
          includeParentObjectives: includingParentObjectives,
        },
      })
        .then((result) => {
          if (result?.data) {
            handleBranchDataLoaded(result.data, nodeId);
          }
        })
        .finally(() => {
          if (nodeId) {
            setLoadingNodeIds((prev) => {
              const newState = { ...prev };
              delete newState[nodeId];
              return newState;
            });
          }
        });
    },
    [loadBranchQuery, handleBranchDataLoaded]
  );

  const contextValue = {
    ...state,
    loading: state.loading || branchLoading,
    error: state.error,
    loadBranch,
    handleRootDataLoaded,
    handleAppendRootData,
    loadingNodeIds,
    dispatch,
  };

  return (
    <GoalsExploreContext.Provider value={contextValue}>
      {children}
    </GoalsExploreContext.Provider>
  );
};

GoalsExploreProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default GoalsExploreProvider;
